import { PHRASES } from "../content/phrases";
import type { DailyCardId } from "../content/dailycards";
import { PLAQUES } from "../content/plaques";
import { TWISTERS } from "../content/twisters";
import { addDays } from "../lib/date";
import type { Profile } from "../state/profile";
import { isMastered } from "./mastery";
import { rankFor } from "./rank";
import { masteryFor } from "./soundmap";

const EN_TWISTER_IDS = TWISTERS.filter((t) => t.lang === "en").map((t) => t.id);
const SV_TWISTER_IDS = TWISTERS.filter((t) => t.lang === "sv").map((t) => t.id);
const EN_PHRASE_IDS = PHRASES.filter((p) => p.lang === "en").map((p) => p.id);
const SV_PHRASE_IDS = PHRASES.filter((p) => p.lang === "sv").map((p) => p.id);
const LEVEL3_TWISTER_IDS = new Set(TWISTERS.filter((t) => t.level === 3).map((t) => t.id));

function masteredCount(profile: Profile, ids: string[]): number {
  return ids.filter((id) => isMastered(profile.cards[id])).length;
}

/** Extra facts about the session/moment that just happened, which aren't
    derivable from cumulative Profile state alone. Callers (the Booth, in
    later phases) fill in whichever fields are relevant; everything is
    optional so cumulative-only predicates ignore this entirely. */
export type PlaqueContext = {
  sessionHour?: number; // local hour the session started (0-23) — dawn/night secrets
  sessionAllCleanNoRetries?: boolean; // every item graded Easy, no retries — "one-take"
  sessionAllEasy?: boolean; // "clean-sweep"
  sessionDailyCardId?: DailyCardId | null;
  sessionDailyCardClean?: boolean; // the daily card's effect was fully realized cleanly
  cardFirstPassLevel3FastCleared?: boolean; // "full-speed-first-try"
  cardJustMasteredWasTwiceLapsed?: boolean; // "vendetta"
};

type Predicate = (profile: Profile, ctx: PlaqueContext, today?: string) => boolean;

const PREDICATES: Record<string, Predicate> = {
  // ---- Milestone ----
  "sessions-1": (p) => p.sessions >= 1,
  "sessions-10": (p) => p.sessions >= 10,
  "sessions-50": (p) => p.sessions >= 50,
  "sessions-100": (p) => p.sessions >= 100,
  "sessions-365": (p) => p.sessions >= 365,
  "streak-3": (p) => p.streak.longest >= 3,
  "streak-7": (p) => p.streak.longest >= 7,
  "streak-30": (p) => p.streak.longest >= 30,
  "streak-100": (p) => p.streak.longest >= 100,
  "streak-365": (p) => p.streak.longest >= 365,
  "minutes-60": (p) => p.seconds >= 60 * 60,
  "minutes-300": (p) => p.seconds >= 300 * 60,
  "minutes-1000": (p) => p.seconds >= 1000 * 60,
  "rank-5": (p) => rankFor(p.clarity).tier >= 5,
  "rank-9": (p) => rankFor(p.clarity).tier >= 9,
  "rank-12": (p) => rankFor(p.clarity).tier >= 12,

  // ---- Skill ----
  "phonation-25": (p) => p.bests.phonationSec >= 25,
  "full-speed-first-try": (_p, ctx) => !!ctx.cardFirstPassLevel3FastCleared,
  "rhythm-95": (p) => p.bests.plosiveAccuracy >= 95,
  "sound-map-8": (p) => Object.keys(p.phonemes).filter((id) => masteryFor(id, p) >= 5).length >= 8,
  "long-memory": (p) => Object.values(p.cards).filter((c) => c.interval >= 60).length >= 30,
  "two-tongues": (p) => masteredCount(p, EN_TWISTER_IDS.concat(EN_PHRASE_IDS)) > 0 && masteredCount(p, SV_TWISTER_IDS.concat(SV_PHRASE_IDS)) > 0,
  "clean-sweep": (_p, ctx) => !!ctx.sessionAllEasy,
  "wpm-200": (p) => Object.values(p.bests.wpmByCard).some((wpm) => wpm >= 200),
  "steady-90": (p) => p.bests.steadinessPct >= 90,
  "silver-tongue-en": (p) => masteredCount(p, EN_TWISTER_IDS) >= 15,
  guldtunga: (p) => masteredCount(p, SV_TWISTER_IDS) >= 15,
  "fluent-one-lang": (p) => masteredCount(p, EN_PHRASE_IDS) >= EN_PHRASE_IDS.length || masteredCount(p, SV_PHRASE_IDS) >= SV_PHRASE_IDS.length,

  // ---- Secret ----
  "dawn-patrol": (_p, ctx) => ctx.sessionHour !== undefined && ctx.sessionHour < 6,
  "night-shift": (_p, ctx) => ctx.sessionHour !== undefined && ctx.sessionHour >= 23,
  "bilingual-week": (p, _ctx, today?: string) => hasBilingualWeek(p, today ?? mostRecentRingDay(p)),
  vendetta: (_p, ctx) => !!ctx.cardJustMasteredWasTwiceLapsed,
  "one-take": (_p, ctx) => !!ctx.sessionAllCleanNoRetries,
  comeback: (p) => hasComeback(p),
  "polyglot-perfect": (_p, ctx) => ctx.sessionDailyCardId === "polyglot" && !!ctx.sessionDailyCardClean,
  "tongue-twister": (p) => hasFiveLevel3MastersInAWeek(p),
};

function mostRecentRingDay(p: Profile): string | null {
  const days = Object.keys(p.rings).sort();
  return days.length ? days[days.length - 1]! : null;
}

function hasBilingualWeek(p: Profile, today: string | null): boolean {
  if (!today) return false;
  for (let i = 0; i < 7; i++) {
    const day = addDays(today, -i);
    const langs = p.rings[day]?.langs ?? [];
    if (!langs.includes("en") || !langs.includes("sv")) return false;
  }
  return true;
}

function hasComeback(p: Profile): boolean {
  const days = Object.keys(p.rings).sort();
  for (let i = 1; i < days.length; i++) {
    const gap = Math.round((new Date(days[i]! + "T12:00:00").getTime() - new Date(days[i - 1]! + "T12:00:00").getTime()) / 86400000);
    if (gap >= 15) return true;
  }
  return false;
}

function hasFiveLevel3MastersInAWeek(p: Profile): boolean {
  const masteredDates = Object.entries(p.cards)
    .filter(([id, c]) => LEVEL3_TWISTER_IDS.has(id) && isMastered(c) && c.masteredAt)
    .map(([, c]) => c.masteredAt!)
    .sort();
  for (let i = 0; i + 4 < masteredDates.length; i++) {
    const start = masteredDates[i]!;
    const end = masteredDates[i + 4]!;
    const gap = Math.round((new Date(end + "T12:00:00").getTime() - new Date(start + "T12:00:00").getTime()) / 86400000);
    if (gap <= 6) return true;
  }
  return false;
}

/** Numeric progress toward the easy-to-quantify milestone plaques, for the
    "closest plaque" progress bar on Today/Progress. Skill/secret plaques
    aren't a simple fraction, so they're not covered here. */
const MILESTONE_PROGRESS: Record<string, (p: Profile) => number> = {
  "sessions-1": (p) => p.sessions,
  "sessions-10": (p) => p.sessions,
  "sessions-50": (p) => p.sessions,
  "sessions-100": (p) => p.sessions,
  "sessions-365": (p) => p.sessions,
  "streak-3": (p) => p.streak.longest,
  "streak-7": (p) => p.streak.longest,
  "streak-30": (p) => p.streak.longest,
  "streak-100": (p) => p.streak.longest,
  "streak-365": (p) => p.streak.longest,
  "minutes-60": (p) => Math.floor(p.seconds / 60),
  "minutes-300": (p) => Math.floor(p.seconds / 60),
  "minutes-1000": (p) => Math.floor(p.seconds / 60),
};

const MILESTONE_TARGET: Record<string, number> = {
  "sessions-1": 1,
  "sessions-10": 10,
  "sessions-50": 50,
  "sessions-100": 100,
  "sessions-365": 365,
  "streak-3": 3,
  "streak-7": 7,
  "streak-30": 30,
  "streak-100": 100,
  "streak-365": 365,
  "minutes-60": 60,
  "minutes-300": 300,
  "minutes-1000": 1000,
};

export function progressFor(plaqueId: string, profile: Profile): { current: number; target: number } | null {
  const getCurrent = MILESTONE_PROGRESS[plaqueId];
  const target = MILESTONE_TARGET[plaqueId];
  if (!getCurrent || target === undefined) return null;
  return { current: Math.min(getCurrent(profile), target), target };
}

/** The nearest not-yet-earned quantifiable milestone, for a single
    "closest plaque" card — never show more than one, scarcity keeps it
    motivating. */
export function closestMilestone(profile: Profile): { id: string; current: number; target: number; fraction: number } | null {
  let best: { id: string; current: number; target: number; fraction: number } | null = null;
  for (const id of Object.keys(MILESTONE_TARGET)) {
    if (profile.plaques[id]) continue;
    const p = progressFor(id, profile);
    if (!p) continue;
    const fraction = p.current / p.target;
    if (!best || fraction > best.fraction) best = { id, ...p, fraction };
  }
  return best;
}

export type PlaqueUnlockEvent = { type: "plaque"; id: string };

/** Checks every not-yet-earned plaque and mutates in newly-earned ones.
    Returns events for the reward queue. Pure aside from the profile clone. */
export function sweepPlaques(profile: Profile, today: string, ctx: PlaqueContext = {}): { profile: Profile; events: PlaqueUnlockEvent[] } {
  const p: Profile = structuredClone(profile);
  const events: PlaqueUnlockEvent[] = [];
  for (const plaque of PLAQUES) {
    if (p.plaques[plaque.id]) continue;
    const predicate = PREDICATES[plaque.id];
    if (!predicate) continue;
    if (predicate(p, ctx, today)) {
      p.plaques[plaque.id] = { at: today };
      events.push({ type: "plaque", id: plaque.id });
    }
  }
  return { profile: p, events };
}
